import { gql, useMutation } from '@apollo/client';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from 'lexical';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect } from 'react';

import { prepareParams } from '../../image-upload/image-upload';
import { INSERT_IMAGE_COMMAND } from '../../rich-lexical-editor/plugins/ImagePlugin';

const PRESIGN = gql`
  mutation GetPresign($input: PresignInput!) {
    getPresign(input: $input) {
      url
    }
  }
`;

const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

interface EnhancedClipboardImagePluginProps {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  useServerUpload?: boolean;
}

export default function EnhancedClipboardImagePlugin({
  maxWidth = 1200,
  maxHeight = 800,
  quality = 0.8,
  useServerUpload = true,
}: EnhancedClipboardImagePluginProps): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const [presign] = useMutation(PRESIGN);

  const resizeImage = useCallback(
    (file: File, maxWidth: number, maxHeight?: number): Promise<File> => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = document.createElement('img');
        let objectUrl: string | null = null;

        if (!ctx) {
          resolve(file);
          return;
        }

        const cleanup = () => {
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
          }
          // Clear canvas to free memory
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }
          // Remove references
          img.onload = null;
          img.onerror = null;
          img.src = '';
        };

        img.onload = () => {
          const { width, height } = img;
          const aspectRatio = height / width;

          let newWidth = width;
          let newHeight = height;

          if (width > maxWidth || (maxHeight && height > maxHeight)) {
            if (width > height) {
              newWidth = maxWidth;
              newHeight = maxWidth * aspectRatio;
            } else if (maxHeight) {
              newHeight = maxHeight;
              newWidth = maxHeight / aspectRatio;
            }
          }

          canvas.width = newWidth;
          canvas.height = newHeight;

          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          canvas.toBlob(
            (blob) => {
              cleanup();
              if (blob) {
                const resizedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                resolve(file);
              }
            },
            file.type,
            quality
          );
        };

        img.onerror = () => {
          cleanup();
          resolve(file);
        };

        objectUrl = URL.createObjectURL(file);
        img.src = objectUrl;
      });
    },
    [quality]
  );

  const uploadImageToServer = useCallback(
    async (file: File): Promise<string> => {
      try {
        let fileToUpload = file;

        // Resize image if needed
        if (maxWidth && file.type.startsWith('image/')) {
          fileToUpload = await resizeImage(file, maxWidth, maxHeight);
        }

        // Get presigned URL
        const params = {
          storage: 'store',
          metadata: {
            size: fileToUpload.size,
            filename: fileToUpload.name,
            mime: fileToUpload.type,
          },
        };

        const { data } = await presign({ variables: { input: params } });
        const url = data?.getPresign.url;

        if (!url) {
          throw new Error('Failed to get upload URL');
        }

        // Upload file to server
        await fetch(url, {
          method: 'PUT',
          body: fileToUpload,
        });

        // Return the public URL
        const uploadedFile = prepareParams(fileToUpload as any, new URL(url));
        return uploadedFile.url || '';
      } catch (error) {
        console.error('Failed to upload image to server:', error);
        throw error;
      }
    },
    [presign, resizeImage, maxWidth, maxHeight]
  );

  const processImageFile = useCallback(
    async (file: File): Promise<string> => {
      if (useServerUpload) {
        try {
          return await uploadImageToServer(file);
        } catch (error) {
          console.warn(
            'Server upload failed, falling back to data URL:',
            error
          );
          // Fallback to data URL if server upload fails
        }
      }

      // Fallback: convert to data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          reject(
            new Error(
              t('error_reading_image_file') || 'Error reading image file'
            )
          );
        };
        reader.readAsDataURL(file);
      });
    },
    [useServerUpload, uploadImageToServer, t]
  );

  const handleClipboardPaste = useCallback(
    async (event: ClipboardEvent): Promise<boolean> => {
      const clipboardData = event.clipboardData;

      if (!clipboardData) {
        return false;
      }

      const { items } = clipboardData;

      // Check if any clipboard items are images
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.kind === 'file' && SUPPORTED_IMAGE_TYPES.includes(item.type)) {
          const file = item.getAsFile();

          if (file) {
            try {
              // Process the image file and get URL
              const imageUrl = await processImageFile(file);

              // Create a meaningful alt text
              const timestamp = Date.now();
              const extension = file.type.split('/')[1] || 'png';
              const altText = `Pasted ${extension.toUpperCase()} image ${new Date(
                timestamp
              ).toLocaleString()}`;

              // Insert the image using the existing image command
              editor.update(() => {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  src: imageUrl,
                  altText: altText,
                });
              });

              // Prevent default paste behavior since we handled the image
              event.preventDefault();
              return true;
            } catch (error) {
              console.warn('Failed to process clipboard image:', error);
              // Show user-friendly error message here if needed
              return false;
            }
          }
        }
      }

      return false;
    },
    [editor, processImageFile]
  );

  const handleModernClipboardPaste = useCallback(async (): Promise<boolean> => {
    // Check if the modern Clipboard API is available
    if (!navigator.clipboard || !navigator.clipboard.read) {
      return false;
    }

    try {
      const clipboardItems = await navigator.clipboard.read();

      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (SUPPORTED_IMAGE_TYPES.includes(type)) {
            const blob = await clipboardItem.getType(type);
            const timestamp = Date.now();
            const extension = type.split('/')[1] || 'png';
            const file = new File(
              [blob],
              `clipboard-image-${timestamp}.${extension}`,
              { type }
            );

            try {
              const imageUrl = await processImageFile(file);
              const altText = `Pasted ${extension.toUpperCase()} image ${new Date(
                timestamp
              ).toLocaleString()}`;

              editor.update(() => {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  src: imageUrl,
                  altText: altText,
                });
              });

              return true;
            } catch (error) {
              console.warn('Failed to process modern clipboard image:', error);
              return false;
            }
          }
        }
      }
    } catch (error) {
      // Clipboard access denied or not supported
      console.warn('Modern clipboard access failed:', error);
      return false;
    }

    return false;
  }, [editor, processImageFile]);

  useEffect(() => {
    const unregister = editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // Check if there are any image items
        const hasImages = Array.from(clipboardData.items).some(
          (item) =>
            item.kind === 'file' && SUPPORTED_IMAGE_TYPES.includes(item.type)
        );

        if (hasImages) {
          // Handle async processing without blocking
          handleClipboardPaste(event);
          return true; // Indicate we're handling this event
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      unregister();
    };
  }, [editor, handleClipboardPaste]);

  // Add keyboard shortcut listener for Ctrl+V to handle modern clipboard API
  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        // Try modern clipboard API first
        const handled = await handleModernClipboardPaste();
        if (handled) {
          event.preventDefault();
        }
      }
    };

    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener('keydown', handleKeyDown);

      return () => {
        editorElement.removeEventListener('keydown', handleKeyDown);
      };
    }
    return () => 0;
  }, [editor, handleModernClipboardPaste]);

  return null;
}
